<script setup lang="ts">
import {
  Dialog,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import { XIcon } from '@heroicons/vue/outline';
import { ChevronDownIcon, PlusSmIcon } from '@heroicons/vue/solid';
import { ref, watch, computed } from 'vue';
import { useWebSocket } from '@vueuse/core';
import { useInfiniteScroll } from '~/composables/infinite-scroll';
import { getApiRoot } from '~/utilities/api';
import { BASE_TITLE } from '~/utilities/constants';

useHead({
  title: `Events ${BASE_TITLE}`,
  meta: [{ property: 'og:title', content: `Events ${BASE_TITLE}` }],
});

const route = useRoute();
const router = useRouter();

const config = useRuntimeConfig();

const mobileFiltersOpen = ref(false);

const routeTypes = route.query?.type;
const routeCollectionSelect = route.query?.collection;

const types = ref(Array.isArray(routeTypes) ? routeTypes : [routeTypes || 2]);
const typeOptions = [
  { value: 0, label: 'activity.mint' },
  { value: 1, label: 'activity.transfer' },
  { value: 2, label: 'activity.trade' },
  { value: 3, label: 'activity.burn' },
];

const parseRouteParam = (param: string | string[]): string => {
  return Array.isArray(param) ? param[0] : param;
};

const radioSelectedCollection = ref(parseRouteParam(routeCollectionSelect) || '');

const typeQuery = computed(() => {
  if (types.value.length === 0) {
    return '';
  }
  return '?' + types.value.map((type) => `type=${type}`).join('&');
});
const collectionQuery = computed(() => {
  if (radioSelectedCollection.value === '') {
    return '';
  }
  return `&collection=${radioSelectedCollection.value}`;
});

watch(types, (types) => {
  router.replace({
    path: route.path,
    query: { ...route.query, type: types },
  });
});
watch(radioSelectedCollection, (radioSelectedCollection) => {
  router.replace({
    path: route.path,
    query: { ...route.query, collection: radioSelectedCollection || undefined },
  });
});

const target = ref();
const { items: events } = useInfiniteScroll(
  target,
  () => `${getApiRoot()}/events${typeQuery.value}${collectionQuery.value}`,
  [typeQuery, collectionQuery],
  12
);

if (process.client) {
  const toTimestamp = (dateString: string): number => new Date(dateString).getTime();

  const { status, data } = useWebSocket(config.public.eventsWebsocketUrl, {
    autoReconnect: true,
  });
  watch(data, () => {
    const newEvent = JSON.parse(data.value);
    console.log({ event: newEvent });
    if (
      types.value.includes(String(newEvent.type)) &&
      (!radioSelectedCollection.value || newEvent.collection?.id === radioSelectedCollection.value)
    ) {
      const oldestEvent = events.value[events.value.length - 1];
      if (toTimestamp(newEvent.timestamp) >= toTimestamp(oldestEvent.timestamp)) {
        events.value = [newEvent, ...events.value.filter((event) => event.id !== newEvent.id)].sort(
          (a, b) => toTimestamp(b.timestamp) - toTimestamp(a.timestamp)
        );
      }
    }
  });
  watch(status, () => {
    console.log({ status: status.value });
  });
}
</script>

<template>
  <div>
    <Container>
      <!-- Mobile filter dialog -->
      <TransitionRoot as="template" :show="mobileFiltersOpen">
        <Dialog as="div" class="relative z-50 lg:hidden" @close="mobileFiltersOpen = false">
          <TransitionChild
            as="template"
            enter="transition-opacity ease-linear duration-300"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <div class="bg-opacity-25 fixed inset-0 bg-black dark:bg-white" />
          </TransitionChild>
          <div class="fixed inset-0 z-50 flex">
            <TransitionChild
              as="template"
              enter="transition ease-in-out duration-300 transform"
              enter-from="translate-x-full"
              enter-to="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leave-from="translate-x-0"
              leave-to="translate-x-full"
            >
              <DialogPanel
                class="relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-white py-4 pb-6 shadow-xl dark:bg-black"
              >
                <div class="flex items-center justify-between px-4">
                  <h2 class="text-lg font-medium text-neutral-900 dark:text-neutral-50">
                    {{ $t('activity.filters') }}
                  </h2>
                  <button
                    type="button"
                    class="-mr-2 flex h-10 w-10 items-center justify-center p-2 text-neutral-400 hover:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500"
                    @click="mobileFiltersOpen = false"
                  >
                    <span class="sr-only">{{ $t('activity.close_menu') }}</span>
                    <XIcon class="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <!-- mobile Filters -->
                <form class="mt-4">
                  <Disclosure
                    v-slot="{ open }"
                    as="div"
                    class="border-t border-neutral-200 pb-4 pt-4 dark:border-neutral-700"
                  >
                    <fieldset>
                      <legend class="w-full px-2">
                        <DisclosureButton
                          class="flex w-full items-center justify-between p-2 text-neutral-400 hover:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500"
                        >
                          <span class="text-sm font-medium text-neutral-900 dark:text-neutral-50">{{
                            $t('activity.event_type')
                          }}</span>
                          <span class="ml-6 flex h-7 items-center">
                            <ChevronDownIcon
                              :class="[open ? '-rotate-180' : 'rotate-0', 'h-5 w-5 transform']"
                              aria-hidden="true"
                            />
                          </span>
                        </DisclosureButton>
                      </legend>
                      <DisclosurePanel class="px-4 pb-2 pt-4">
                        <div class="space-y-5">
                          <div v-for="(option, optionIdx) in typeOptions" :key="option.value" class="flex items-center">
                            <input
                              :id="`type-${optionIdx}-mobile`"
                              v-model="types"
                              :name="`type[]`"
                              :value="option.value"
                              type="checkbox"
                              class="h-4 w-4 rounded border-neutral-300 text-black focus:ring-black dark:border-neutral-600 dark:text-white"
                            />
                            <label
                              :for="`type-${optionIdx}-mobile`"
                              class="ml-3 text-sm text-neutral-500 dark:text-neutral-400"
                            >
                              {{ $t(option.label) }}
                            </label>
                          </div>
                        </div>
                      </DisclosurePanel>
                    </fieldset>
                  </Disclosure>
                  <Disclosure
                    v-slot="{ open }"
                    as="div"
                    class="border-t border-neutral-200 pb-4 pt-4 dark:border-neutral-700"
                  >
                    <fieldset>
                      <legend class="w-full px-2">
                        <DisclosureButton
                          class="flex w-full items-center justify-between p-2 text-neutral-400 hover:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500"
                        >
                          <span class="text-sm font-medium text-neutral-900 dark:text-neutral-50">{{
                            $t('activity.collections')
                          }}</span>
                          <span class="ml-6 flex h-7 items-center">
                            <ChevronDownIcon
                              :class="[open ? '-rotate-180' : 'rotate-0', 'h-5 w-5 transform']"
                              aria-hidden="true"
                            />
                          </span>
                        </DisclosureButton>
                      </legend>
                      <DisclosurePanel class="px-4 pb-2 pt-4">
                        <CollectionFilter v-model="radioSelectedCollection" />
                      </DisclosurePanel>
                    </fieldset>
                  </Disclosure>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </TransitionRoot>
      <main class="mx-auto md:py-8">
        <div class="flex flex-col gap-x-8 pt-12 lg:flex-row">
          <aside>
            <h2 class="sr-only">{{ $t('activity.filters') }}</h2>
            <button type="button" class="inline-flex items-center lg:hidden" @click="mobileFiltersOpen = true">
              <span class="text-sm font-medium text-neutral-900 dark:text-neutral-50">{{
                $t('activity.filters')
              }}</span>
              <PlusSmIcon
                class="ml-1 h-5 w-5 flex-shrink-0 text-neutral-400 dark:text-neutral-500"
                aria-hidden="true"
              />
            </button>
            <div class="hidden w-48 lg:block">
              <!-- Side-menu filters -->
              <form class="space-y-10 divide-y divide-neutral-200 dark:divide-neutral-700" @submit.prevent="">
                <div class="pt-2">
                  <fieldset>
                    <legend class="block text-sm font-medium text-neutral-900 dark:text-neutral-50">
                      {{ $t('activity.event_type') }}
                    </legend>
                    <div class="space-y-3 pt-6">
                      <div v-for="(option, optionIdx) in typeOptions" :key="option.value" class="flex items-center">
                        <label :for="`type-${optionIdx}`" class="flex-1 text-sm text-neutral-600 dark:text-neutral-300">
                          {{ $t(option.label) }}
                        </label>
                        <input
                          :id="`type-${optionIdx}`"
                          v-model="types"
                          :name="`type[]`"
                          :value="option.value"
                          type="checkbox"
                          class="h-4 w-4 rounded border border-neutral-300 text-black focus:ring-black dark:border-neutral-600 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
                        />
                      </div>
                    </div>
                  </fieldset>
                </div>
                <div class="pt-10">
                  <!-- Collection filter on the left side  -->
                  <fieldset class="block">
                    <legend class="block text-sm font-medium text-neutral-900 dark:text-neutral-50">
                      {{ $t('activity.collections') }}
                    </legend>
                    <CollectionFilter v-model="radioSelectedCollection" class="pt-6" />
                  </fieldset>
                </div>
              </form>
            </div>
          </aside>

          <!-- Product grid -->
          <div v-if="events" class="mt-6 flex-1 lg:mt-0">
            <div class="mb-2 ml-1 flex items-center">
              <div class="relative inline-flex">
                <span class="flex h-2 w-2">
                  <span
                    class="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"
                  ></span>
                  <span class="relative inline-flex h-2 w-2 rounded-full bg-emerald-600"></span>
                </span>
              </div>
              <span class="ml-1.5 text-sm font-semibold text-emerald-600"> Live </span>
            </div>
            <EventsTable :events="events" />
            <div ref="target" />
          </div>
        </div>
      </main>
    </Container>
  </div>
</template>
